<template>
  <div>
    <VWidget
      :progress="progress"
      :title="$t('Images')"
    >
      <div slot="widget-header-action">
        <v-btn
          flat
          class="text--secondary text-capitalize"
          no-title-update
          @click="openDialog"
        >
          {{ $t('Add Image') }}
        </v-btn>
      </div>

      <v-layout
        slot="widget-content"
        wrap
        class="pb-4"
      >
        <v-flex>
          <span class="title">{{ $t('Default image') }}</span><br>
          <v-img
            :src="$helpers.imageFrom(product, 'small')"
            class="mt-3"
            width="100"
            aspect-ratio="1"
          />

        </v-flex>
        <v-flex
          xs12
          class="mb-3"
        >
          <span
            v-if="product.images.length > 0"
            class="subttle"
          >{{ $t('Select default image') }}
          </span><br>
          <!--    DO NOT REMOVE, TO BE USED LATER!    -->
          <!--          <span-->
          <!--            v-if="!hasDefaultImage"-->
          <!--            class="default-image-warning">{{ $t('Default image not selected, placeholder will be used instead') }}</span>-->
        </v-flex>
        <v-flex>
          <span
            v-for="(img, i) in product.images"
            :key="`image-${i}`"
            class="mr-4"
          >
            <v-badge
              color="red "
              right
              overlap
            >
              <template v-slot:badge>
                <v-icon
                  color="white"
                  class="selectable"
                  @click="deleteImage(img)"
                >
                  close
                </v-icon>
              </template>
              <v-img
                :src="$helpers.imageFrom(img.url)"
                :class="{'default': product.image_url === img.url}"
                width="100"
                aspect-ratio="1"
                class="selectable mb-4"
                @click="setDefault(img)"
              />
            </v-badge>
          </span>
        </v-flex>
      </v-layout>
    </VWidget>

    <v-dialog
      v-if="dialog"
      v-model="dialog"
      persistent
      max-width="600px"
    >
      <v-card>
        <v-card-title>
          {{ stepsTitle[activeStep] }}
        </v-card-title>

        <v-card-text
          v-if="activeStep === 1"
          class="text-xs-center"
        >
          <v-btn
            color="primary"
            @click="selectMethod('url')"
          >
            {{ $t('Provide Image url') }}
          </v-btn>
          <UploadButton
            :loading="progress"
            :title="$t('Upload image file')"
            class="file-upload-button"
            multiple
            no-title-update
            @file-update="update"
          />
        </v-card-text>

        <v-card-text v-if="activeStep === 2 && selectedMethod === 'url'">
          <v-text-field
            v-model="newImageUrl"
            :rules="imageRules"
            :label="$t('Image url')"
            outline
          />
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            v-if="activeStep === 2"
            color="darken-1"
            flat
            small
            @click="activeStep = 1"
          >
            {{ $t('Back') }}
          </v-btn>
          <v-btn
            color="darken-1"
            flat
            small
            @click="closeDialog"
          >
            {{ $t('Close') }}
          </v-btn>
          <v-btn
            v-if="activeStep === 2 && selectedMethod === 'url'"
            :loading="progress"
            color="primary darken-1"
            flat
            small
            @click="saveImageByUrl"
          >
            {{ $t('Save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import UploadButton from 'vuetify-upload-button'

export default {
  name: 'ProductSingleImages',

  components: {
    UploadButton
  },

  props: {
    product: {
      type: Object,
      required: true
    }
  },

  data () {
    return {
      dialog: false,
      progress: false,
      newImageUrl: '',
      activeStep: 1,
      selectedMethod: ''
    }
  },

  computed: {
    hasDefaultImage () {
      if (this.product.image_url) {
        return true
      }

      return this.product.images && this.product.images.find(img => img.url === this.product.image_url)
    },

    imageRules () {
      return [
        v => !!v || this.$t('Image url is required'),
        v => /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/
          .test(v) || this.$t('Image url must be valid')
      ]
    },

    stepsTitle () {
      return {
        1: this.$t('Select Method'),
        2: this.$t('Save Image')
      }
    }
  },

  methods: {
    setDefault (image) {
      this.product.images.forEach(img => { img.default = img.id === image.id })
      this.product.image_url = image.url
    },

    async deleteImage (image) {
      if (this.product.id) {
        const imageDeleted = this.$http.make('product:image', { 'id': image.id })
        await imageDeleted.for(this.product).delete()
      }
      this.product.images = this.product.images.filter(item => item.url !== image.url)
      if (!this.product.images) {
        this.product.image_url = undefined
      }
    },

    update (files) {
      Object.keys(files).forEach(index => {
        this.progress = true
        this.upload(files[index])
          .then(async res => {
            // File uploaded successfully
            const response = res.data
            const url = response.secure_url

            const image = this.$http.make('product:image', {
              url,
              default: false,
              product_uuid: this.product.uuid
            })

            if (this.product.id) {
              await image.for(this.product).save()
            }
            this.progress = false
            this.product.images.push({
              id: image.id,
              product_id: this.product.id,
              sort_order: 0,
              url: image.url
            })
            this.closeDialog()
          })
          .catch(error => {
            this.$toast.error(this.$t(error.message))
          })
      })
    },

    async upload (file) {
      const cloudinary = await this.$http.make('general_settings').getCloudinary()
      const url = `https://api.cloudinary.com/v1_1/${cloudinary.name}/upload`

      const fd = new FormData()
      fd.append('upload_preset', cloudinary.preset)
      fd.append('tags', 'browser_upload') // Optional - add tag for image admin in Cloudinary
      fd.append('file', file)

      return require('axios').post(url, fd)
    },

    async saveImageByUrl () {
      this.progress = true

      const image = this.$http.make('product:image', {
        url: this.newImageUrl,
        default: false,
        product_uuid: this.product.uuid
      })

      if (this.product.id) {
        try {
          await image.for(this.product).save()
        } catch (error) {
          this.$toast.error(this.$t(error.message))
          this.progress = false
          return
        }
      }

      this.progress = false
      this.newImageUrl = ''
      this.product.images.push({
        id: image.id,
        product_id: this.product.id,
        sort_order: 0,
        url: image.url
      })
      this.closeDialog()
    },

    openDialog () {
      this.dialog = true
    },

    closeDialog () {
      this.dialog = false
      this.activeStep = 1
      this.newImageUrl = ''
    },

    selectMethod (method) {
      this.selectedMethod = method
      this.activeStep = 2
    }
  }
}
</script>

<style lang="stylus" scoped>
  .selectable
    cursor: pointer
    paddiing-right: -10px
  .default
    border: 1px solid
    box-shadow: 0 0.5em 1em rgba(0,0,0,0.2)
  .default-image-warning
    color: red
  .file-upload-button
    display:inline
</style>
